import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  getFleetSnapshot,
  getOEMDistribution,
  getCityDistribution,
  getClientDistribution,
  getHubLocationDistribution,
  type OEMDistributionItem,
  type CityDistributionItem,
  type ClientDistributionItem,
  type HubLocationDistributionItem,
} from '@/services/api/dashboard';
import {
  OEMDistributionModal,
  OEMDistributionData,
  CityDistributionModal,
  CityDistributionData,
  ClientsDistributionModal,
  ClientsDistributionData,
  HubLocationModal,
  HubLocationData,
  FleetCard,
  DistributionMetric,
  FleetUtilizationChart,
  OEMDistributionChart,
  VehiclesByCityChart,
  BusinessUnitChart,
  ClientsByActiveEVChart,
  HubLocationChart
} from './components';

const PAGINATION_CONSTANTS = {
  API_FETCH_SIZE: 20, // Fetch data from API per page (Lazy Load)
} as const;

interface FleetSnapshot {
  totalVehicles: number;
  operationalUtilization: number;
  fleetHealthScore: number;
  activeRepoCases: number;
}

interface PaginationState {
  loading: boolean;
  hasMore: boolean;
  total: number;
  page: number;
}

type DistributionType = 'oem' | 'city' | 'clients' | 'hub';
type DashboardTab = 'fleet' | 'financial';

const DASHBOARD_TABS: Array<{ id: DashboardTab; label: string }> = [
  { id: 'fleet', label: 'Fleet Overview' },
  { id: 'financial', label: 'Financial Overview' },
];

const Dashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<DashboardTab>('fleet');

  // Modal states
  const [isOemModalOpen, setIsOemModalOpen] = useState(false);
  const [isCityModalOpen, setIsCityModalOpen] = useState(false);
  const [isClientsModalOpen, setIsClientsModalOpen] = useState(false);
  const [isHubModalOpen, setIsHubModalOpen] = useState(false);

  // Fleet snapshot data state
  const [fleetSnapshot, setFleetSnapshot] = useState<FleetSnapshot>({
    totalVehicles: 0,
    operationalUtilization: 0,
    fleetHealthScore: 0,
    activeRepoCases: 0,
  });
  const [snapshotLoading, setSnapshotLoading] = useState(false);

  // Pagination states organized by type
  const [paginationStates, setPaginationStates] = useState<Record<DistributionType, PaginationState>>({
    oem: { loading: false, hasMore: true, total: 0, page: 1 },
    city: { loading: false, hasMore: true, total: 0, page: 1 },
    clients: { loading: false, hasMore: true, total: 0, page: 1 },
    hub: { loading: false, hasMore: true, total: 0, page: 1 },
  });

  // Modal data organized by type
  const [modalData, setModalData] = useState({
    oem: [] as OEMDistributionItem[],
    city: [] as CityDistributionItem[],
    clients: [] as ClientDistributionItem[],
    hub: [] as HubLocationDistributionItem[],
  });

  // Prevent multiple initialization calls
  const hasInitialized = useRef(false);

  const updatePaginationState = useCallback((type: DistributionType, updates: Partial<PaginationState>) => {
    setPaginationStates(prev => ({
      ...prev,
      [type]: { ...prev[type], ...updates }
    }));
  }, []);

  // Fetch fleet snapshot data
  const fetchFleetSnapshot = useCallback(async () => {
    try {
      setSnapshotLoading(true);
      const response = await getFleetSnapshot();
      const data = response.data;

      setFleetSnapshot({
        totalVehicles: Number(data.totalVehicles) || 0,
        operationalUtilization: Number(data.operationalUtilization) || 0,
        fleetHealthScore: Number(data.fleetHealthScore) || 0,
        activeRepoCases: Number(data.activeRepoCases) || 0,
      });
    } catch (error) {
      console.error('Error fetching fleet snapshot:', error);
    } finally {
      setSnapshotLoading(false);
    }
  }, []);

  // Generic fetch function for all distribution data
  const fetchDistributionData = useCallback(async (
    type: DistributionType,
    page: number = 1,
    isLoadMore: boolean = false
  ) => {
    try {
      updatePaginationState(type, { loading: true });

      const size = PAGINATION_CONSTANTS.API_FETCH_SIZE;
      const params = { page, size };

      let response;
      switch (type) {
        case 'oem':
          response = await getOEMDistribution(params);
          break;
        case 'city':
          response = await getCityDistribution(params);
          break;
        case 'clients':
          response = await getClientDistribution(params);
          break;
        case 'hub':
          response = await getHubLocationDistribution(params);
          break;
        default:
          throw new Error(`Unknown distribution type: ${type}`);
      }

      // Extract data from response
      const list: any[] = Array.isArray(response.data) ? response.data : response.data?.items || [];
      const pagination = response.pagination;

      const totalVal = pagination?.total ?? list.length;
      const lastPage = pagination?.lastPage ?? Math.ceil(Number(totalVal) / size);
      const hasMoreVal = page < lastPage;

      setModalData(prev => ({
        ...prev,
        [type]: isLoadMore ? [...prev[type], ...list] : list
      }));

      // Update state including the current page
      updatePaginationState(type, {
        total: Number(totalVal) || 0,
        hasMore: hasMoreVal,
        loading: false,
        page: page
      });

    } catch (error) {
      console.error(`Error fetching ${type} distribution data:`, error);
      updatePaginationState(type, { hasMore: false, loading: false });
      if (!isLoadMore) {
        setModalData(prev => ({ ...prev, [type]: [] }));
      }
    }
  }, [updatePaginationState]);

  // Initialize dashboard - only fetch fleet snapshot
  const initializeDashboard = useCallback(() => {
    fetchFleetSnapshot();
  }, [fetchFleetSnapshot]);

  // Load data on mount - single call only
  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      initializeDashboard();
    }
  }, [initializeDashboard]);

  // Modal handlers
  const handleModalOpen = useCallback((type: DistributionType) => {
    // Fetch data only when modal is opening
    updatePaginationState(type, { page: 1, hasMore: true });
    fetchDistributionData(type, 1, false);

    switch (type) {
      case 'oem': setIsOemModalOpen(true); break;
      case 'city': setIsCityModalOpen(true); break;
      case 'clients': setIsClientsModalOpen(true); break;
      case 'hub': setIsHubModalOpen(true); break;
    }
  }, [fetchDistributionData, updatePaginationState]);

  const handleLoadMore = useCallback((type: DistributionType) => {
    const currentState = paginationStates[type];
    if (currentState.loading || !currentState.hasMore) return;

    const nextPage = currentState.page + 1;
    fetchDistributionData(type, nextPage, true);
  }, [paginationStates, fetchDistributionData]);

  // Transform modal data for components
  const getTransformedData = useCallback(() => {
    return {
      oem: modalData.oem.map((oem): OEMDistributionData => ({
        oemName: oem.oemName || 'Unknown',
        totalVehicles: Number(oem.totalVehicle) || 0,
        percentageTotal: oem.percentageOfTotal !== undefined
          ? Math.round(oem.percentageOfTotal * 10) / 10
          : 0,
        activeVehicles: Number(oem.active) || 0,
        idleVehicles: Number(oem.idle) || 0,
        inRepairVehicles: Number(oem.inRepair) || 0,
        brokenVehicles: Number(oem.broken) || 0,
        lostVehicles: Number(oem.lost) || 0,
      })),
      city: modalData.city.map((city): CityDistributionData => ({
        city: city.city || 'Unknown City',
        totalVehicle: Number(city.totalVehicle) || 0,
        percentageTotal: Number(city.percentageOfTotal) || 0,
        growthMoM: Number(city.growthMoM) || 0,
        top3Clients: Array.isArray(city.top3Clients) && city.top3Clients.length > 0
          ? city.top3Clients.join(', ')
          : 'N/A',
      })),
      clients: modalData.clients.map((client): ClientsDistributionData => ({
        client: client.clientName || 'Unknown',
        pitstop: client.hubName || 'Unknown Hub',
        active: Number(client.totalVehicle) || 0,
        percentageTotal: Number(client.percentageOfTotal) || 0,
      }))
    };
  }, [modalData]);

  // Handle See All clicks
  const handleOemSeeAll = useCallback(() => {
    handleModalOpen('oem');
  }, [handleModalOpen]);

  const handleCitySeeAll = useCallback(() => {
    handleModalOpen('city');
  }, [handleModalOpen]);

  const handleClientsSeeAll = useCallback(() => {
    handleModalOpen('clients');
  }, [handleModalOpen]);

  const handleHubSeeAll = useCallback(() => {
    handleModalOpen('hub');
  }, [handleModalOpen]);

  // Hub modal data transformed
  const hubModalData: HubLocationData[] = useMemo(() =>
    modalData.hub.map((item): HubLocationData => ({
      hubName: item.hubName || 'Unknown Hub',
      totalVehicles: Number(item.totalVehicle) || 0,
      percentageTotal: Number(item.percentageOfTotal) || 0,
      activeVehicles: Number(item.active) || 0,
      idleVehicles: Number(item.idle) || 0,
      inRepairVehicles: Number(item.inRepair) || 0,
      brokenVehicles: Number(item.broken) || 0,
      lostVehicles: Number(item.lost) || 0,
    })),
  [modalData.hub]);

  // Get transformed data
  const transformedData = getTransformedData();

  const renderFleetOverview = () => (
    <div className="w-full">
      <div className="mb-4">
        <h2 className="text-[24px] font-semibold text-gray-900 mb-1">Fleet Overview</h2>
        <p className="text-[14px] text-gray-600">High-level summary of all fleet operations and metrics.</p>
      </div>

      <div className="mb-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <FleetCard
            metric={DistributionMetric.TOTAL_VEHICLES}
            value={fleetSnapshot.totalVehicles}
            loading={snapshotLoading}
          />

          <FleetCard
            metric={DistributionMetric.ACTIVE_REPO_CASE}
            value={fleetSnapshot.activeRepoCases}
            loading={snapshotLoading}
          />

          <FleetCard
            metric={DistributionMetric.OPERATIONAL_UTILIZATION}
            value={fleetSnapshot.operationalUtilization}
            formatAsPercentage={true}
            loading={snapshotLoading}
          />

          <FleetCard
            metric={DistributionMetric.FLEET_HEALTH_SCORE}
            value={fleetSnapshot.fleetHealthScore}
            formatAsPercentage={true}
            loading={snapshotLoading}
          />
        </div>
      </div>

      <div className="mt-4 mb-6">
        <FleetUtilizationChart />
      </div>

      <div className="mt-6 pt-8">
        <div className="mb-4">
          <h2 className="text-[24px] font-semibold text-gray-900 mb-1">Fleet Distribution</h2>
          <p className="text-[14px] text-gray-600">View fleet composition by OEM and by City.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-5">
          <div className="relative">
            <OEMDistributionChart onSeeAll={handleOemSeeAll} />
          </div>
          <div className="relative lg:col-span-2">
            <VehiclesByCityChart onSeeAll={handleCitySeeAll} />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 mb-6">
          <div className="relative">
            <BusinessUnitChart />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <div className="relative lg:col-span-2">
            <ClientsByActiveEVChart
              onSeeAll={handleClientsSeeAll}
            />
          </div>
          <div className="relative h-full">
            <HubLocationChart onSeeAll={handleHubSeeAll} />
          </div>
        </div>
      </div>
    </div>
  );

  const renderFinancialOverview = () => (
    <div className="w-full">
      <div className="mb-6">
        <h2 className="text-[24px] font-semibold text-gray-900 mb-1">Financial Overview</h2>
        <p className="text-[14px] text-gray-600">A comprehensive breakdown of your fleet’s financial performance, revenue trends, and operational costs.</p>
      </div>

      <div className="rounded-2xl border border-gray-200 bg-gradient-to-br from-white to-gray-50 p-8">
        <div className="max-w-2xl">
          <span className="inline-flex items-center rounded-full bg-[#f3ecff] px-3 py-1 text-xs font-medium text-[#5e2aac]">
            Ready for financial widgets
          </span>
          <h3 className="mt-4 text-[20px] font-semibold text-gray-900">Financial dashboard section prepared</h3>
          <p className="mt-2 text-[14px] leading-6 text-gray-600">
            This tab is now separated from fleet operations and ready to host billing, recovery cost,
            maintenance spending, or profitability metrics once those APIs are connected.
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="bg-white p-6 max-w-full mx-auto">
      <div className="mb-6">
        <h1 className="text-[30px] font-semibold text-gray-900 mb-1">Dashboard</h1>
        <p className="text-[14px] text-gray-600">
          Gain real-time insights into your fleet&apos;s operational status, geographical distribution, and financial performance.
        </p>
      </div>

      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8" role="tablist" aria-label="Dashboard sections">
            {DASHBOARD_TABS.map((tab) => {
              const isActive = activeTab === tab.id;

              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                    isActive
                      ? 'border-[#5e2aac] text-[#5e2aac]'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {activeTab === 'fleet' ? renderFleetOverview() : renderFinancialOverview()}

      {/* OEM Distribution Modal */}
      <OEMDistributionModal
        isOpen={isOemModalOpen}
        onClose={() => setIsOemModalOpen(false)}
        data={transformedData.oem}
        loading={paginationStates.oem.loading}
        hasMore={paginationStates.oem.hasMore}
        onLoadMore={() => handleLoadMore('oem')}
      />

      {/* City Distribution Modal */}
      <CityDistributionModal
        isOpen={isCityModalOpen}
        onClose={() => setIsCityModalOpen(false)}
        data={transformedData.city}
        loading={paginationStates.city.loading}
        hasMore={paginationStates.city.hasMore}
        onLoadMore={() => handleLoadMore('city')}
      />

      {/* Clients Distribution Modal */}
      <ClientsDistributionModal
        isOpen={isClientsModalOpen}
        onClose={() => setIsClientsModalOpen(false)}
        data={transformedData.clients}
        loading={paginationStates.clients.loading}
        hasMore={paginationStates.clients.hasMore}
        onLoadMore={() => handleLoadMore('clients')}
      />

      {/* Hub Location Modal */}
      <HubLocationModal
        isOpen={isHubModalOpen}
        onClose={() => setIsHubModalOpen(false)}
        data={hubModalData}
        loading={paginationStates.hub.loading}
        hasMore={paginationStates.hub.hasMore}
        onLoadMore={() => handleLoadMore('hub')}
      />
    </div>
  );
};

export default Dashboard;
